import { z } from 'zod';
import { HttpMethod } from '../enums/HttpMethod';
import { TaskStatus } from '../enums/TaskStatus';
import { isBlank, isPlainObject, parseJson } from '../utils';

const cronRegex = /^([0-5]?\d|\*)(\s+([0-5]?\d|\*)){4}$/;

const baseTaskSchema = z.object({
  name: z
    .string()
    .min(3, { message: 'Debe tener al menos 3 caracteres' })
    .max(100, { message: 'No debe superar 100 caracteres' }),
  cronExpression: z.string().regex(cronRegex, {
    message: 'Formato de cron inválido (5 campos numéricos o *)',
  }),
  url: z.string().url({ message: 'Debe ser una URL válida' }),
  method: z.nativeEnum(HttpMethod),
  headersText: z.string().default(''),
  bodyText: z.string().default(''),
  authRequired: z.boolean().default(true),
  status: z.nativeEnum(TaskStatus).default(TaskStatus.ACTIVE),
});

export const taskSchema = baseTaskSchema.superRefine((values, ctx) => {
  if (!isBlank(values.headersText)) {
    const parsed = parseJson(values.headersText);
    if (!parsed.ok) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['headersText'],
        message: `JSON inválido: ${parsed.error}`,
      });
    } else if (!isPlainObject(parsed.value)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['headersText'],
        message: 'Los headers deben ser un objeto, por ej. {"X-Api-Key": "abc"}',
      });
    }
  }

  if (isBlank(values.bodyText)) return;

  const parsedBody = parseJson(values.bodyText);
  if (!parsedBody.ok) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['bodyText'],
      message: `JSON inválido: ${parsedBody.error}`,
    });
    return;
  }

  // En GET el body viaja como query params, así que tiene que ser un objeto.
  if (values.method === HttpMethod.GET && !isPlainObject(parsedBody.value)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['bodyText'],
      message: 'En GET los parámetros deben ser un objeto clave/valor',
    });
  }
});

export type TaskSchema = z.infer<typeof taskSchema>;

export const defaultTaskValues: TaskSchema = {
  name: '',
  cronExpression: '* * * * *',
  url: 'https://api.example.com',
  method: HttpMethod.GET,
  headersText: '',
  bodyText: '',
  authRequired: true,
  status: TaskStatus.ACTIVE,
};
